import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  customerReportSectionQuery,
  customerReportShellQuery,
  customerReportTableQuery,
  downloadCustomerReportPdf,
  type CustomerReportSection,
} from '../../api/settingsCustomerReport'
import { escapeHtml } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { LoadFailed } from '../../ui/LoadFailed'
import { CompanyReportsFrame } from './CompanyReportsPage'
import { ReportRange } from './ModuleReportPage'

/**
 * Settings -> Reports -> Customer Reports: `settings/company_reports/customer_report`.
 *
 * MACHETELE VECHI, element cu element:
 *  - `views/version-3/settings/company_reports/customer_report/index.php`:
 *    titlul, casuta de perioada (`.reportrange`, daterangepicker - aici
 *    `ReportRange`, aceeasi ca la filele pe module) si tabelul
 *    `#customer-report-details` (`CustomerReportModel` din
 *    `public/js/settings/company_reports.js`: 9 coloane, ordonat dupa firma);
 *  - `.../customer_report/actionTabs.php` + `actionTabs-1.js`: randul
 *    desfasurat cu cele patru file (`ul.action_tabs li a.action_button`), prima
 *    deschisa singura, si butonul "Export PDF" de sub continut;
 *  - continutul fiecarei file e HTML randat pe server DIN CHIAR MACHETELE
 *    PORTALULUI (`pdf/newSummary.php`, `workstation_support.php`...), pus in
 *    `.details_content` ca in `actionTabs-1.js`. "Click to see all ..." din
 *    tabelele lungi merge la fel: randurile `.hide_tickets` se arata / ascund.
 *
 * Perioada pleaca cu fiecare cerere (`from`/`to`), ca `filterFrom`/`filterTo`
 * din casutele ascunse ale machetei.
 */

const COLUMNS: TableColumn[] = [
  {
    index: 0,
    title: '',
    orderable: false,
    visible: true,
    width: 30,
    html: (row) =>
      `<center class="show_details color-dark-grey pointer font-16" data-id="${escapeHtml(companyOfRow(row))}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
  },
  { index: 1, title: 'Company', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.name) },
  { index: 2, title: 'All Devices', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.devices) },
  { index: 3, title: 'Servers', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.servers) },
  { index: 4, title: 'Incidents', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.incidents) },
  { index: 5, title: 'User Request', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.user_request) },
  { index: 6, title: 'Proactive Monitoring', orderable: true, visible: true, className: 'a-left', html: (row) => escapeHtml(row.proactive) },
  { index: 7, title: 'Workstation Effort', orderable: true, visible: true, className: 'a-left', html: (row) => `${escapeHtml(row.workstation_effort)} %` },
  { index: 8, title: 'NOC Effort', orderable: true, visible: true, className: 'a-left', html: (row) => `${escapeHtml(row.noc_effort)} %` },
]

/**
 * Firma randului desfasurat.
 *
 * `getCustomerReportDetails` scrie `ID` pe fiecare rand (si `DT_RowId`,
 * `row_<ID>`, pentru DataTables). `Number(row.ID)` singur se face `NaN` daca
 * randul vine vreodata fara `ID`, iar `NaN` in adresa
 * (`/customer/NaN/summary`) nu se potriveste cu `company => \d+` din ruta:
 * serverul raspunde 404 „Not Found", desi firma exista. Se citesc amandoua
 * campurile pe care capatul le scrie chiar, si cand niciunul nu da un numar
 * se SPUNE, in loc sa se ceara o adresa care nu poate raspunde.
 */
export function companyOfRow(row: Record<string, unknown>): number {
  const direct = Number(row.ID)
  if (Number.isInteger(direct) && direct > 0) return direct
  const marked = /^row_(\d+)$/.exec(String(row.DT_RowId ?? ''))

  return marked ? Number(marked[1]) : 0
}

/** Randul desfasurat: filele si continutul lor (`actionTabs.php`). */
function CompanyTabs({ company, sections, from, to }: { company: number; sections: CustomerReportSection[]; from: string; to: string }) {
  const [current, setCurrent] = useState(sections[0]?.id ?? 'summary')
  const [busy, setBusy] = useState(false)
  const content = useQuery(customerReportSectionQuery(company, current, from, to))
  const host = useRef<HTMLDivElement>(null)

  /* `.ticket_report_see_all` din `actionTabs-1.js`: arata / ascunde randurile `.hide_tickets`. */
  useEffect(() => {
    const root = host.current
    if (!root) return
    const onClick = (event: Event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.ticket_report_see_all')
      if (!target) return
      const hidden = target.parentElement?.parentElement?.querySelectorAll<HTMLElement>('.hide_tickets') ?? []
      const expanded = target.classList.toggle('expanded')
      target.textContent = expanded ? 'Click to hide ...' : 'Click to see all ...'
      hidden.forEach((tr) => {
        tr.style.display = expanded ? '' : 'none'
      })
    }
    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [])

  /*
   * PANOUL NU MINTE. Patru stari deosebite, fiindca omul face altceva la
   * fiecare: fara drept - ecranul de „No access rights"; firma care nu mai e
   * in lista (404) - i se spune sa reincarce; orice alta cadere - motivul si
   * „Retry"; si o sectiune care chiar n-are nimic de aratat - randul de gol al
   * machetelor portalului (`nothing_message`), nu un panou alb care seamana cu
   * o cadere. Pana acum toate patru aratau la fel: fie un „note-danger" fara
   * iesire, fie o rotita care nu se mai oprea.
   */
  const notFound = content.error instanceof ApiError && content.error.status === 404

  const exportPdf = () => {
    setBusy(true)
    downloadCustomerReportPdf(company, from, to)
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : 'The PDF could not be produced.', 'Export PDF', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs" data-company={company}>
          {sections.map((section) => (
            <li key={section.id}>
              <a
                href="#"
                className={'action_button' + (section.id === current ? ' active' : '')}
                data-type={section.type}
                onClick={(event) => {
                  event.preventDefault()
                  setCurrent(section.id)
                }}
              >
                {section.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content" ref={host}>
          {company <= 0 ? (
            <LoadFailed what="The report of this company" error={new Error('The list did not carry a company number for this row.')} />
          ) : isForbidden(content.error) ? (
            <Forbidden />
          ) : content.error ? (
            <LoadFailed
              what={notFound ? 'The report of this company' : 'This section of the report'}
              error={notFound ? new Error('It is not in your list any more - reload the page.') : content.error}
              onRetry={() => void content.refetch()}
            />
          ) : !content.data ? (
            <Spinner />
          ) : (
            <>
              {content.data.html.trim() === '' ? (
                <p className="nothing_message">Nothing to show for this period ... </p>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: content.data.html }} />
              )}
              <button type="button" className="btn btn-primary export_report_button" disabled={busy} onClick={exportPdf}>
                {busy ? 'Preparing…' : 'Export PDF'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function CustomerReportPage() {
  const shell = useQuery(customerReportShellQuery)
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null)
  const [state, setState] = useTableState('customer-report-details', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'asc' },
    search: '',
  })

  const from = period?.from ?? shell.data?.today ?? ''
  const to = period?.to ?? shell.data?.today ?? ''

  const table = useQuery({
    ...customerReportTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: state.search,
      filterFrom: from,
      filterTo: to,
    }),
    placeholderData: keepPreviousData,
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />

  return (
    <CompanyReportsFrame current="customer">
      <div id="content">
        <h4>Customer Report</h4>
        <hr />
        <div className="row" style={{ marginBottom: 20 }}>
          <div className="col-md-2 pull-right">
            <ReportRange from={from} to={to} onChange={(f, t) => setPeriod({ from: f, to: t })} />
          </div>
        </div>
        {shell.error ? (
          <LoadFailed what="The customer report" error={shell.error} onRetry={() => void shell.refetch()} />
        ) : !shell.data ? (
          <Spinner />
        ) : (
          <DataTable
            id="customer-report-details"
            columns={COLUMNS}
            rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(row) => String(row.DT_RowId ?? row.ID)}
            details={(row) => <CompanyTabs company={companyOfRow(row)} sections={shell.data.sections} from={from} to={to} />}
          />
        )}
      </div>
    </CompanyReportsFrame>
  )
}
